/**
 * Money outside the drawer: what a till handed over, received and counted; general cash
 * counted; cash taken to the bank; the bank and the wallet squared with their statements.
 *
 * The drawer was always counted, blind, at every corte. What left it was not: a retiro or the
 * corte's takeout went to general cash on the books the moment the cashier said so, nobody
 * confirmed receiving it, and nobody ever counted general cash. A cashier could record a C$ 500
 * retiro, keep it, and every corte balanced. Now money nobody took in hand waits in «Efectivo por
 * recibir» on its till, and becomes general cash only as the owner counts it; general cash
 * itself can be counted against the books; and so can the bank and the wallet.
 */

import {
  centavos,
  esCuentaEnDolares,
  fechaEnNicaragua,
  fxRate,
  negMoney,
  orderKey,
  subMoney,
  usdToCordobas,
  type AnyDomainEvent,
  type Centavos,
  type CuentaBanco,
  type FxRate,
  type Settings,
} from '@abastece/shared';
import { ACCOUNTS } from '../ledger/accounts.js';
import { credit, debit, entry, type JournalLine } from '../ledger/journal.js';
import type { ApplyResult, Reader, SesionCaja, TransferenciaBanco } from '../state.js';
import { alOficial, cuentaCerradaParaSiempre, lineaDeBanco } from './bancos.js';
import { conciliada, contada, contadaDespues, lugarEnElLog, yaContada, yaLaContaron } from './contadas.js';

export async function reduceEfectivo(event: AnyDomainEvent, read: Reader): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'efectivo.recibido': {
      const p = event.payload;
      const sesion = await read.sesionCaja(p.sesionId);
      if (!sesion) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'La caja no existe' }] };
      }
      const porRecibir = sesion.porRecibir ?? 0;
      const porRecibirDolares = sesion.porRecibirDolares ?? 0;
      if (porRecibir === 0 && porRecibirDolares === 0) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa caja no tiene nada por recibir' }] };
      }

      // Over and short net into one figure, as at the corte, and it is the till's: «Sobrante /
      // Faltante de caja», on the shift that handed it over. The rate's is not (`alRecibir`).
      const tasa = fxRate((await read.config()).fxTasaOficial);
      const { recibido, esperado, diferencia, cambiaria } = alRecibir(sesion, { cordobas: p.contadoCordobas, dolares: p.contadoDolares }, tasa);
      const lineas: JournalLine[] = [
        debit('CAJA_FUERTE', recibido, 'Contado al recibirlo'),
        credit('EFECTIVO_POR_RECIBIR', esperado),
      ];
      if (diferencia > 0) lineas.push(credit('SOBRANTE_CAJA', diferencia));
      if (diferencia < 0) lineas.push(debit('FALTANTE_CAJA', negMoney(diferencia), 'Faltó al recibirlo'));
      if (cambiaria > 0) lineas.push(credit('GANANCIA_CAMBIARIA', cambiaria));
      if (cambiaria < 0) lineas.push(debit('PERDIDA_CAMBIARIA', negMoney(cambiaria)));

      return {
        effects: [
          {
            kind: 'sesion.patch',
            id: sesion.id,
            rev,
            patch: {
              porRecibir: centavos(0),
              porRecibirDolares: centavos(0),
              porRecibirValor: centavos(0),
              recibidoPor: event.actorId,
              recibidoEn: event.clientTs,
              diferenciaRecibida: centavos((sesion.diferenciaRecibida ?? 0) + diferencia),
              // Counted twice, the first count stays; one that stood leaves an earlier receipt's.
              ...(p.primerConteoCordobas !== undefined ? { primerConteoRecibidoCordobas: centavos(p.primerConteoCordobas) } : {}),
              ...(p.primerConteoDolares !== undefined ? { primerConteoRecibidoDolares: centavos(p.primerConteoDolares) } : {}),
            },
          },
          {
            kind: 'asiento',
            entry: entry(
              event.id,
              fechaEnNicaragua(event.clientTs),
              `Efectivo recibido de la caja del ${fechaEnNicaragua(sesion.abiertaEn)}${p.nota ? `: ${p.nota}` : ''}`,
              lineas,
            ),
          },
        ],
        rejections: [],
      };
    }

    case 'efectivo.arqueo': {
      const p = event.payload;
      const config = await read.config();
      const tasa = fxRate(config.fxTasaOficial);
      const contado = centavos(p.contadoCordobas + usdToCordobas(centavos(p.contadoDolares), tasa));
      const esperado = await read.saldoCuenta(ACCOUNTS.CAJA_FUERTE.code);
      const diferencia = subMoney(contado, esperado);
      /*
       * The first count sets where the safe starts, against capital (`inicial`); later ones find
       * a surplus or a shortage. The phone says it is the first by what it has heard of, and
       * another phone may have counted before it synced: the books go by their own note of the
       * counts (`cuentasContadas`), and a second «first count» is what it found missing or over
       * since the first.
       */
      const inicial = p.inicial === true && !yaContada(config, 'CAJA_FUERTE');
      const marca = contada(config, 'CAJA_FUERTE', lugarEnElLog(event), rev);
      if (diferencia === 0) return { effects: [marca], rejections: [] };
      const [sobra, falta] = inicial ? (['CAPITAL', 'CAPITAL'] as const) : (['SOBRANTE_GENERAL', 'FALTANTE_GENERAL'] as const);
      return {
        effects: [
          marca,
          {
            kind: 'asiento',
            entry: entry(
              event.id,
              fechaEnNicaragua(event.clientTs),
              `${inicial ? 'Saldo inicial del efectivo general, contado por primera vez' : 'Arqueo de efectivo general'}${p.nota ? `: ${p.nota}` : ''}`,
              diferencia > 0
                ? [debit('CAJA_FUERTE', diferencia, inicial ? 'Lo que había' : 'Sobrante al contar'), credit(sobra, diferencia)]
                : [debit(falta, negMoney(diferencia), inicial ? 'Lo que faltaba' : 'Faltante al contar'), credit('CAJA_FUERTE', negMoney(diferencia))],
            ),
          },
        ],
        rejections: [],
      };
    }

    case 'efectivo.depositado': {
      const p = event.payload;
      if (p.cordobas === 0 && p.dolares === 0) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Un depósito lleva córdobas, dólares o los dos' }] };
      }
      if (await read.deposito(p.depositoId)) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Ese depósito ya está registrado' }] };
      }
      const config = await read.config();
      const cerrada = cuentasDelDeposito(config, p);
      if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };
      // Dollars leave general cash at the official rate, which is what it carries them at, and go
      // onto the dollar account at the same: nothing is gained or lost on the way to the bank.
      const tasa = fxRate(config.fxTasaOficial);
      const cordobas = centavos(p.cordobas);
      const dolares = centavos(p.dolares);
      const valorDolares = usdToCordobas(dolares, tasa);
      const cuenta = p.cuenta ?? 'BANCOS';
      const cuentaDolares = p.cuentaDolares ?? 'BANCO_DOLARES';
      const lineas: JournalLine[] = [];
      if (cordobas > 0) lineas.push(debit(cuenta, cordobas, 'Depósito en córdobas'));
      if (valorDolares > 0) lineas.push(lineaDeBanco('debe', cuentaDolares, valorDolares, dolares, 'Depósito en dólares'));
      lineas.push(credit('CAJA_FUERTE', centavos(cordobas + valorDolares), 'Al banco'));
      return {
        effects: [
          {
            kind: 'deposito.upsert',
            id: p.depositoId,
            row: {
              id: p.depositoId,
              fecha: p.fecha,
              cordobas,
              dolares,
              valorDolares,
              cuenta: cordobas > 0 ? cuenta : null,
              cuentaDolares: dolares > 0 ? cuentaDolares : null,
              referencia: p.referencia ?? null,
              nota: p.nota ?? null,
              lugar: lugarEnElLog(event),
              rev,
            },
          },
          {
            kind: 'asiento',
            entry: entry(
              event.id,
              p.fecha,
              `Depósito en el banco${p.referencia ? ` (boleta ${p.referencia})` : ''}${p.nota ? `: ${p.nota}` : ''}`,
              lineas,
            ),
          },
        ],
        rejections: [],
      };
    }

    /*
     * Cash brought from the bank: a deposit the other way, kept with them (`delBanco`). Dollars
     * leave the dollar account at the official rate and go into general cash at the same, as a
     * deposit's do on the way there. Not held against what the books say the bank holds: a
     * transfer that came in unrecorded is in the bank all the same, and its statement squares it.
     */
    case 'efectivo.sacado_del_banco': {
      const p = event.payload;
      if (p.cordobas === 0 && p.dolares === 0) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Lo que se saca del banco lleva córdobas, dólares o los dos' }] };
      }
      if (await read.deposito(p.depositoId)) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Eso que se sacó del banco ya está registrado' }] };
      }
      const config = await read.config();
      const cerrada = cuentasDelDeposito(config, p);
      if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };
      const tasa = fxRate(config.fxTasaOficial);
      const cordobas = centavos(p.cordobas);
      const dolares = centavos(p.dolares);
      const valorDolares = usdToCordobas(dolares, tasa);
      const cuenta = p.cuenta ?? 'BANCOS';
      const cuentaDolares = p.cuentaDolares ?? 'BANCO_DOLARES';
      const lineas: JournalLine[] = [debit('CAJA_FUERTE', centavos(cordobas + valorDolares), 'Del banco')];
      if (cordobas > 0) lineas.push(credit(cuenta, cordobas, 'Sacado en córdobas'));
      if (valorDolares > 0) lineas.push(lineaDeBanco('haber', cuentaDolares, valorDolares, dolares, 'Sacado en dólares'));
      return {
        effects: [
          {
            kind: 'deposito.upsert',
            id: p.depositoId,
            row: {
              id: p.depositoId,
              fecha: p.fecha,
              cordobas,
              dolares,
              valorDolares,
              cuenta: cordobas > 0 ? cuenta : null,
              cuentaDolares: dolares > 0 ? cuentaDolares : null,
              referencia: p.referencia ?? null,
              nota: p.nota ?? null,
              delBanco: true,
              lugar: lugarEnElLog(event),
              rev,
            },
          },
          {
            kind: 'asiento',
            entry: entry(
              event.id,
              p.fecha,
              `Sacado del banco${p.referencia ? ` (${p.referencia})` : ''}${p.nota ? `: ${p.nota}` : ''}`,
              lineas,
            ),
          },
        ],
        rejections: [],
      };
    }

    /*
     * A deposit that was not. Before general cash is counted again, or the account it went to is
     * squared with a statement: once either is, that count found the money where it really was,
     * and voiding it would move it on the books a second time (`contadas.ts`).
     */
    case 'efectivo.deposito_anulado': {
      const p = event.payload;
      const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
      const deposito = await read.deposito(p.depositoId);
      if (!deposito) return rechazo('Ese depósito no existe');
      if (deposito.anulado) return rechazo('Ese depósito ya fue anulado');
      const config = await read.config();
      const cuenta = (deposito.cuenta ?? 'BANCOS') as CuentaBanco;
      const cuentaDolares = (deposito.cuentaDolares ?? 'BANCO_DOLARES') as CuentaBanco;
      const contadas = (['CAJA_FUERTE', cuenta, cuentaDolares] as const).filter(
        (c, i) =>
          (i === 0 || (i === 1 ? deposito.cordobas : deposito.dolares) > 0) &&
          contadaDespues(config, c, deposito.lugar ?? deposito.rev, deposito.fecha),
      );
      const delBanco = deposito.delBanco === true;
      if (contadas.length > 0) return rechazo(yaLaContaron(contadas[0]!, delBanco ? 'sacar eso del banco' : 'ese depósito', config));
      const total = centavos(deposito.cordobas + deposito.valorDolares);
      // Each account gets back what it gave: general cash what went to the bank, or the bank what came from it.
      const lineas: JournalLine[] = delBanco
        ? [
            ...(deposito.cordobas > 0 ? [debit(cuenta, deposito.cordobas, 'Vuelve al banco')] : []),
            ...(deposito.valorDolares > 0 ? [lineaDeBanco('debe', cuentaDolares, deposito.valorDolares, deposito.dolares, 'Vuelve al banco')] : []),
            credit('CAJA_FUERTE', total),
          ]
        : [
            debit('CAJA_FUERTE', total, 'Vuelve del banco'),
            ...(deposito.cordobas > 0 ? [credit(cuenta, deposito.cordobas)] : []),
            ...(deposito.valorDolares > 0 ? [lineaDeBanco('haber', cuentaDolares, deposito.valorDolares, deposito.dolares)] : []),
          ];
      return {
        effects: [
          { kind: 'deposito.patch', id: deposito.id, rev, patch: { anulado: true, motivoAnulacion: p.motivo } },
          {
            kind: 'asiento',
            // On the deposit's own day, as a voided expense is: the money never left that day.
            entry: entry(
              event.id,
              deposito.fecha,
              delBanco
                ? `Anulación de lo sacado del banco${deposito.referencia ? ` (${deposito.referencia})` : ''}: ${p.motivo}`
                : `Anulación de depósito${deposito.referencia ? ` (boleta ${deposito.referencia})` : ''}: ${p.motivo}`,
              lineas,
            ),
          },
        ],
        rejections: [],
      };
    }

    // A mark that the owner went over the day; the books are as they were.
    case 'semana.cuadrada':
      return { effects: [], rejections: [] };

    case 'cuenta.conciliada': {
      const p = event.payload;
      const config = await read.config();
      const banco = p.cuenta === 'BILLETERA_MOVIL' ? null : p.cuenta;
      const cerrada = cuentaCerradaParaSiempre(config, banco);
      if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };
      const enDolares = banco !== null && esCuentaEnDolares(banco);
      // A dollar account's statement is in dollars, and it holds what that read from then on.
      const dolares = enDolares ? p.saldoDolares : undefined;
      if (enDolares && dolares === undefined) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Falta cuántos dólares dice el estado de cuenta' }] };
      }
      const codigo = ACCOUNTS[p.cuenta].code;
      /*
       * What the books said on the statement's own date: anything after it is not on the statement.
       * A dollar account's dollars against the statement's, and what they are over or under at the
       * official rate, which is what dollars go onto it at: not the statement converted whole, since
       * each deposit went on converted on its own, nor are dollars that went in at another rate
       * worth today's (`saldoDelEstadoEnDolares`, which is what the phone shows meanwhile).
       */
      let diferencia;
      let difDolares = 0;
      if (enDolares) {
        difDolares = dolares! - (await read.saldoCuentaDolares(codigo, p.fecha));
        const tasa = fxRate(config.fxTasaOficial);
        diferencia = difDolares >= 0 ? alOficial(difDolares, tasa) : negMoney(alOficial(-difDolares, tasa));
      } else {
        diferencia = subMoney(centavos(p.saldo), await read.saldoCuenta(codigo, p.fecha));
      }
      // Squared with its statement: what moved it before, up to its date, it already found (`contadas.ts`).
      const marca = conciliada(config, p.cuenta, { lugar: lugarEnElLog(event), fecha: p.fecha, ...(dolares !== undefined ? { dolares } : {}) }, rev);
      if (diferencia === 0) return { effects: marca, rejections: [] };
      const lado = (cuenta: 'debe' | 'haber', monto: typeof diferencia, memo?: string): JournalLine =>
        banco ? lineaDeBanco(cuenta, banco, monto, Math.abs(difDolares), memo) : cuenta === 'debe' ? debit(p.cuenta, monto, memo) : credit(p.cuenta, monto, memo);
      return {
        effects: [
          ...marca,
          {
            kind: 'asiento',
            entry: entry(
              event.id,
              p.fecha,
              `Conciliación de ${nombreEnConcepto(config, p.cuenta)} con su estado de cuenta${p.nota ? `: ${p.nota}` : ''}`,
              diferencia > 0
                ? [lado('debe', diferencia, 'Más de lo que dicen los libros'), credit('DIFERENCIAS_CONCILIACION', diferencia)]
                : [debit('DIFERENCIAS_CONCILIACION', negMoney(diferencia), 'Menos de lo que dicen los libros'), lado('haber', negMoney(diferencia))],
            ),
          },
        ],
        rejections: [],
      };
    }

    case 'banco.transferido':
      return transferir(event, read, rev);

    case 'banco.transferencia_anulada':
      return anularTransferencia(event, read, rev);

    default:
      return null;
  }
}

/**
 * What a till's money came to as the owner counted it (`efectivo.recibido`). Each currency against
 * what the till handed over of it, the dollars' gap at today's official rate, is the till's, over
 * or short. General cash takes the dollars at what they are worth today, as a corte hands them over
 * (`cerrarSesion`), and what that is other than what they waited at is the rate's, a gain or a loss
 * on exchange: the rate moved since, or each handover put its dollars on to the centavo on its own,
 * and US$ 10 twice at C$ 366.24 are C$ 732.49 together. The receipt used to hold both against the
 * till: the cashier's shortage or surplus for dollars nobody lost (2026-10-20).
 */
export function alRecibir(
  sesion: Pick<SesionCaja, 'porRecibir' | 'porRecibirDolares' | 'porRecibirValor'>,
  contado: { cordobas: number; dolares: number },
  tasa: FxRate,
): { recibido: Centavos; esperado: Centavos; diferencia: Centavos; cambiaria: Centavos } {
  const recibido = centavos(contado.cordobas + usdToCordobas(centavos(contado.dolares), tasa));
  const esperado = centavos((sesion.porRecibir ?? 0) + (sesion.porRecibirValor ?? 0));
  const difDolares = usdToCordobas(centavos(contado.dolares - (sesion.porRecibirDolares ?? 0)), tasa);
  const diferencia = centavos(contado.cordobas - (sesion.porRecibir ?? 0) + difDolares);
  return { recibido, esperado, diferencia, cambiaria: centavos(recibido - esperado - diferencia) };
}

/** A deposit's or a withdrawal's accounts, each only if it carries something: one never opened refuses it. */
function cuentasDelDeposito(
  config: Settings,
  p: { cordobas: number; dolares: number; cuenta?: CuentaBanco | undefined; cuentaDolares?: CuentaBanco | undefined },
): string | null {
  return (
    (p.cordobas > 0 ? cuentaCerradaParaSiempre(config, p.cuenta) : null) ??
    (p.dolares > 0 ? cuentaCerradaParaSiempre(config, p.cuentaDolares) : null)
  );
}

/** An account in an entry's concept: «Conciliación de banco», «… de «BAC dólares»». */
function nombreEnConcepto(config: Settings, cuenta: CuentaBanco | 'BILLETERA_MOVIL'): string {
  if (cuenta === 'BILLETERA_MOVIL') return 'billetera';
  const puesta = config.cuentasBanco.find((c) => c.cuenta === cuenta);
  if (puesta) return `«${puesta.nombre}»`;
  return esCuentaEnDolares(cuenta) ? 'banco en dólares' : 'banco';
}

/**
 * Money from one bank account to another (`banco.transferido`). Each side goes on at what it is in
 * córdobas: a córdoba account at its córdobas, a dollar account at its dollars at the official rate,
 * as a deposit's. Between currencies the two differ by what the bank paid for them against that
 * rate: a gain or a loss on the exchange, as dollars taken at the counter at the store's rate are.
 * Not held against what the books say `desde` holds: its statement is what says that.
 */
async function transferir(
  event: Extract<AnyDomainEvent, { type: 'banco.transferido' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  if (await read.transferenciaBanco(p.transferenciaId)) return rechazo('Esa transferencia ya está registrada');
  const config = await read.config();
  const cerrada = cuentaCerradaParaSiempre(config, p.desde) ?? cuentaCerradaParaSiempre(config, p.hacia);
  if (cerrada) return rechazo(cerrada);
  const tasa = fxRate(config.fxTasaOficial);
  const monto = centavos(p.monto);
  const recibido = centavos(p.recibido ?? p.monto);
  const valor = (cuenta: CuentaBanco, cuanto: typeof monto) => (esCuentaEnDolares(cuenta) ? alOficial(cuanto, tasa) : cuanto);
  const valorDesde = valor(p.desde, monto);
  const valorHacia = valor(p.hacia, recibido);
  const row: TransferenciaBanco = {
    id: p.transferenciaId,
    fecha: p.fecha,
    desde: p.desde,
    hacia: p.hacia,
    monto,
    recibido,
    valorDesde,
    valorHacia,
    referencia: p.referencia ?? null,
    nota: p.nota ?? null,
    lugar: lugarEnElLog(event),
    rev,
  };
  return {
    effects: [
      { kind: 'transferenciaBanco.upsert', id: row.id, row },
      {
        kind: 'asiento',
        entry: entry(
          event.id,
          p.fecha,
          `Transferencia de ${nombreEnConcepto(config, p.desde)} a ${nombreEnConcepto(config, p.hacia)}${p.referencia ? ` (${p.referencia})` : ''}${p.nota ? `: ${p.nota}` : ''}`,
          lineasDeTransferencia(row, 'ida'),
        ),
      },
    ],
    rejections: [],
  };
}

/** A transfer's lines, or the ones undoing it: each account back to what it was, and the exchange with them. */
function lineasDeTransferencia(t: TransferenciaBanco, sentido: 'ida' | 'vuelta'): JournalLine[] {
  const ida = sentido === 'ida';
  const desde = t.desde as CuentaBanco;
  const hacia = t.hacia as CuentaBanco;
  const lineas = [
    lineaDeBanco(ida ? 'debe' : 'haber', hacia, t.valorHacia, t.recibido, ida ? undefined : 'Se anula'),
    lineaDeBanco(ida ? 'haber' : 'debe', desde, t.valorDesde, t.monto, ida ? undefined : 'Vuelve'),
  ];
  // What reached `hacia` was worth more than what left `desde`: a gain; less, a loss.
  const gana = t.valorHacia - t.valorDesde;
  if (gana > 0) lineas.push(ida ? credit('GANANCIA_CAMBIARIA', centavos(gana)) : debit('GANANCIA_CAMBIARIA', centavos(gana)));
  if (gana < 0) lineas.push(ida ? debit('PERDIDA_CAMBIARIA', centavos(-gana)) : credit('PERDIDA_CAMBIARIA', centavos(-gana)));
  return lineas;
}

/**
 * A transfer between accounts that was not: each back to what it held. Not once either was
 * squared with a statement after it: that statement found where the money really was.
 */
async function anularTransferencia(
  event: Extract<AnyDomainEvent, { type: 'banco.transferencia_anulada' }>,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const p = event.payload;
  const rechazo = (reason: string): ApplyResult => ({ effects: [], rejections: [{ eventId: event.id, reason }] });
  const t = await read.transferenciaBanco(p.transferenciaId);
  if (!t) return rechazo('Esa transferencia no existe');
  if (t.anulado) return rechazo('Esa transferencia ya fue anulada');
  const config = await read.config();
  const contada = ([t.desde, t.hacia] as CuentaBanco[]).find((c) => contadaDespues(config, c, t.lugar ?? t.rev, t.fecha));
  if (contada) return rechazo(yaLaContaron(contada, 'esa transferencia', config));
  return {
    effects: [
      { kind: 'transferenciaBanco.patch', id: t.id, rev, patch: { anulado: true, motivoAnulacion: p.motivo } },
      {
        kind: 'asiento',
        // On the transfer's own day, as a voided deposit is: the money never left that day.
        entry: entry(event.id, t.fecha, `Anulación de transferencia entre cuentas: ${p.motivo}`, lineasDeTransferencia(t, 'vuelta')),
      },
    ],
    rejections: [],
  };
}
